import React, { memo, useMemo } from 'react';
import type { CellProps } from 'react-table';
import { useFlexLayout, useRowSelect, useTable } from 'react-table';

import Button from '../../../../../../../../common/Button';
import ControlledCheckbox from '../../../../../../../../common/ControlledCheckbox';
import { useDuplicateAdsStepperContext } from '../../../../DuplicateAdsStepperProvider/DuplicateAdsStepperProvider';
import type { DuplicateLineItem } from '../../../../DuplicateAdsStepperProvider/types';
import { pickerElement, sharedTableColumns } from '../constants';
import DuplicateLineItemsTable from '../DuplicateLineItemsTable/DuplicateLineItemsTable';
import { useConfirmLineItems, useSearchedData } from './hooks';

export type Props = {
  handleClearSearch: () => void;
  searchValue: string;
  adId: string;
};

const DuplicateLineItemsSearchTable = ({ handleClearSearch, searchValue, adId }: Props): React.JSX.Element => {
  const { replaceLineItems, getLineItemsWithInitiallyDeselected } = useDuplicateAdsStepperContext();
  const { onNext, isSearching, searchResults, hasMore } = useSearchedData({ searchValue });

  const lineItemsWithInitiallyDeselected = useMemo(() => getLineItemsWithInitiallyDeselected(adId), [
    adId,
    getLineItemsWithInitiallyDeselected,
  ]);

  const selectedSavedRowIds = useMemo(() => {
    const initialLineItemsSelection: Record<string, boolean> = {};

    lineItemsWithInitiallyDeselected.forEach((lineItem) => {
      if (lineItem.selected) initialLineItemsSelection[lineItem.id] = lineItem.selected;
    });

    return initialLineItemsSelection;
  }, [lineItemsWithInitiallyDeselected]);

  const {
    rows,
    headerGroups,
    prepareRow,
    getTableBodyProps,
    getTableProps,
    state: { selectedRowIds },
    selectedFlatRows,
  } = useTable<DuplicateLineItem>(
    {
      columns: sharedTableColumns,
      data: searchResults,
      getRowId: (originalRow) => originalRow.id,
      autoResetSelectedRows: false,
      initialState: {
        selectedRowIds: selectedSavedRowIds,
      },
    },
    useRowSelect,
    useFlexLayout,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: 'selection',
          Header: ({ getToggleAllRowsSelectedProps }): JSX.Element => (
            <ControlledCheckbox
              {...getToggleAllRowsSelectedProps()}
              className={pickerElement('checkbox')}
              data-testid="checkbox-header-searched-line-items"
            />
          ),
          Cell: ({ row }: CellProps<DuplicateLineItem>): JSX.Element => (
            <ControlledCheckbox
              {...row.getToggleRowSelectedProps()}
              className={pickerElement('checkbox')}
              data-testid="checkbox-cell-searched-line-items"
            />
          ),
          width: 30,
        },
        ...columns,
      ]);
    }
  );

  const { handleConfirm, isDifferentSelection } = useConfirmLineItems({
    selectedFlatRows,
    selectedSavedRowIds,
    selectedRowIds,
    handleClearSearch,
    replaceLineItems,
    rows,
    lineItemsWithInitiallyDeselected,
    adId,
  });

  return (
    <>
      <DuplicateLineItemsTable
        tableProps={getTableProps()}
        headerGroups={headerGroups}
        tableBodyProps={getTableBodyProps()}
        rows={rows}
        prepareRow={prepareRow}
        isSearching={isSearching}
        emptyMessage="No Line Items found."
        hasMore={hasMore}
        onNext={onNext}
      />
      <div className={pickerElement('actions')} data-testid="duplicate-line-items-actions">
        <Button onClick={handleClearSearch} data-testid="duplicate-line-items-cancel-button">
          Decline
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!isDifferentSelection}
          data-testid="duplicate-line-items-confirm-button"
        >
          Confirm
        </Button>
      </div>
    </>
  );
};

export default memo(DuplicateLineItemsSearchTable);
